import {
  Badge,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Button,
  Text,
  useClipboard,
  VStack,
} from '@chakra-ui/react';

import { CopyOutline } from '@aslanpay/portal-common-lib';

import { format, parseISO } from 'date-fns';

import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import {
  FILE_UPLOAD_STATUS_COLOR,
  FILE_UPLOAD_STATUS_LABELS,
  FILE_UPLOAD_TYPE_LABELS,
} from 'app/constants/fileUploads';

import { FileUploadDetailsModalProps } from './FileUploadDetailsModal.types';

interface ReasonDetail {
  label: string;
  value: string;
}

interface ReasonEntry {
  message: string;
  code?: string;
  details: ReasonDetail[];
}

const formatDateTime = (value?: string) =>
  value ? format(parseISO(value), 'dd/MM/yyyy HH:mm') : '-';

const normalizeReason = (reason: unknown): ReasonEntry => {
  if (typeof reason === 'string') {
    return { message: reason, details: [] };
  }

  if (reason && typeof reason === 'object') {
    const obj = reason as Record<string, unknown>;
    const message =
      (typeof obj.message === 'string' && obj.message) ||
      (typeof obj.error === 'string' && obj.error) ||
      JSON.stringify(obj);

    const details: ReasonDetail[] = [];
    if (obj.fieldName != null && obj.fieldValue != null) {
      details.push({ label: String(obj.fieldName), value: String(obj.fieldValue) });
    }

    return {
      message,
      code: typeof obj.code === 'string' ? obj.code : undefined,
      details,
    };
  }

  return { message: String(reason), details: [] };
};

// statusReasons is a JSON-encoded string: a string array, an object with an `errors` array, or a
// single object — falling back to plain text when it isn't valid JSON. Repeated errors are deduped.
const parseStatusReasons = (statusReasons?: string): ReasonEntry[] => {
  if (!statusReasons) {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(statusReasons);
  } catch {
    return [{ message: statusReasons, details: [] }];
  }

  let rawList: unknown[];
  if (Array.isArray(parsed)) {
    rawList = parsed;
  } else if (
    parsed &&
    typeof parsed === 'object' &&
    Array.isArray((parsed as { errors?: unknown }).errors)
  ) {
    rawList = (parsed as { errors: unknown[] }).errors;
  } else {
    rawList = [parsed];
  }

  const seen = new Set<string>();
  return rawList.map(normalizeReason).filter((entry) => {
    const key = `${entry.code ?? ''}|${entry.message}|${entry.details
      .map((detail) => `${detail.label}:${detail.value}`)
      .join(',')}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

// Renders a single reason as a plain-text line suitable for copying and sharing with a developer.
const formatReasonLine = (reason: ReasonEntry): string => {
  const parts: string[] = [];

  if (reason.code) {
    parts.push(`[${reason.code}]`);
  }

  parts.push(reason.message);

  if (reason.details.length > 0) {
    parts.push(
      `(${reason.details.map((detail) => `${detail.label}: ${detail.value}`).join(', ')})`
    );
  }

  return parts.join(' ');
};

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Flex gap="12px" align="flex-start">
      <Text textStyle="smallSemiBold" color="brand.darkGray" minW="120px">
        {label}
      </Text>
      <Box flex="1">{children}</Box>
    </Flex>
  );
}

function FileUploadDetailsModal({ isOpen, onClose, file }: FileUploadDetailsModalProps) {
  const reasons = file ? parseStatusReasons(file.statusReasons) : [];
  const reasonsText = reasons.map(formatReasonLine).join('\n');
  const { hasCopied, onCopy } = useClipboard(reasonsText);

  if (!file) {
    return null;
  }

  const uploadedBy =
    `${file.uploadedByFirstName ?? ''} ${file.uploadedByLastName ?? ''}`.trim() || '-';

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      {/* The shared Modal theme sets dialog maxWidth: 'unset'; cap it with an inline style. */}
      <ModalContent style={{ width: '60vw', maxWidth: '60vw' }}>
        <ModalHeader>File details</ModalHeader>
        <ModalCloseButton data-testid="file-upload-details-modal-close" />
        <ModalBody>
          <VStack align="stretch" spacing="12px">
            <DetailRow label="File name">
              <Text wordBreak="break-all">{file.name}</Text>
            </DetailRow>
            <DetailRow label="Type">
              {FILE_UPLOAD_TYPE_LABELS[file.uploadType] ?? file.uploadType}
            </DetailRow>
            <DetailRow label="Status">
              <Badge colorScheme={FILE_UPLOAD_STATUS_COLOR[file.status] ?? 'gray'} variant="subtle">
                {FILE_UPLOAD_STATUS_LABELS[file.status] ?? file.status}
              </Badge>
            </DetailRow>
            <DetailRow label="Uploaded by">{uploadedBy}</DetailRow>
            <DetailRow label="Uploaded at">{formatDateTime(file.createdAt)}</DetailRow>
            {file.confirmedAt && (
              <DetailRow label="Confirmed at">{formatDateTime(file.confirmedAt)}</DetailRow>
            )}
            {file.payCalendarId && (
              <DetailRow label="Pay calendar">
                <TextWithClipboard value={file.payCalendarId} />
              </DetailRow>
            )}
            <DetailRow label="File ID">
              <TextWithClipboard value={file.id} />
            </DetailRow>

            {reasons.length > 0 && (
              <Box>
                <Flex justify="space-between" align="center" mb="8px">
                  <Text textStyle="smallSemiBold" color="brand.darkGray">
                    Reasons
                  </Text>
                  <Button
                    size="xs"
                    variant="link"
                    leftIcon={<CopyOutline boxSize="16px" />}
                    onClick={onCopy}
                    data-testid="copy-reasons-button"
                  >
                    {hasCopied ? 'Copied' : 'Copy'}
                  </Button>
                </Flex>
                <VStack align="stretch" spacing="8px">
                  {reasons.map((reason) => (
                    <Box
                      key={`${reason.code ?? ''}-${reason.message}-${reason.details
                        .map((detail) => detail.value)
                        .join(',')}`}
                      bg="brand.lightGray2"
                      borderRadius="8px"
                      p="10px 12px"
                    >
                      <Flex gap="8px" align="flex-start">
                        {reason.code && (
                          <Badge colorScheme="red" variant="subtle" flexShrink={0}>
                            {reason.code}
                          </Badge>
                        )}
                        <Text textStyle="smallRegular" color="brand.darkPurple">
                          {reason.message}
                        </Text>
                      </Flex>
                      {reason.details.length > 0 && (
                        <Text textStyle="extraSmallRegular" color="brand.darkGray" mt="4px">
                          {reason.details
                            .map((detail) => `${detail.label}: ${detail.value}`)
                            .join(' · ')}
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { FileUploadDetailsModal };

export default FileUploadDetailsModal;
